import { Grid, Typography } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../components/CustomButton";
import {
  createRoleModulePermissions,
  getRoleModulePermissions,
  updateRoleModulePermissions,
} from "../../../redux/reducers/globalSettingsSlice";
import {
  mergeMandateSessionKeepTimestamp,
  readMandateSession,
} from "../../../util/mandateSessionStorage";
import "../BranchManagement/BranchManagement.css";
import "./GlobalSettings.css";

const rolesList = [
  { name: "Billing", role: "ROLE_BILLING" },
  { name: "Front Desk", role: "ROLE_RECEPTION" },
  { name: "Groomer", role: "ROLE_GROOMER" },
  { name: "Inventory Manager", role: "ROLE_INVENTORY" },
  { name: "Lab/Radiology Technician", role: "ROLE_RADIOLOGY" },
  { name: "Paravet/Nurse", role: "ROLE_DOCTOR" },
  { name: "Pharmacist", role: "ROLE_PHARMACY" },
  { name: "Practice Manager", role: "ROLE_CLINICADMIN" },
  { name: "Veterinarian", role: "ROLE_DOCTOR" },
];

const modulesList = [
  { key: "dashboard", label: "Dashboard/Appointments" },
  { key: "pets", label: "Pets" },
  { key: "vets", label: "Vets" },
  // { key: "appointments", label: "Appointments" }, // kept in sync with dashboard
  { key: "billing", label: "Billing" },
  { key: "inventory", label: "Inventory" },
  { key: "lab", label: "Diagnostics" }, // ROLE_RADIOLOGY
  { key: "pharmacy", label: "Pharmacy" }, // ROLE_PHARMACY
  { key: "plan", label: "Plan" },
  { key: "reports", label: "Reports" },
];

/** Changing any key in a group updates all keys in that group together. */
const LINKED_MODULE_GROUPS = [["dashboard", "appointments", "pets"]];

const getLinkedModules = (moduleKey) => {
  const group = LINKED_MODULE_GROUPS.find((keys) => keys.includes(moduleKey));
  return group || [moduleKey];
};

const syncLinkedModules = (modules = {}) => {
  const next = { ...modules };
  LINKED_MODULE_GROUPS.forEach((keys) => {
    // Prefer the first module that has an explicit value in the group
    const level =
      keys.map((key) => next[key]).find((val) => val != null && val !== "") ??
      "full";
    keys.forEach((key) => {
      next[key] = level;
    });
  });
  return next;
};

const accessLevels = [
  { id: "none", label: "No access", badge: "—", color: "#94a3b8" },
  { id: "read", label: "Read only", badge: "R", color: "#3b82f6" },
  // { id: "write", label: "Read & write", badge: "RW", color: "#f59e0b" },
  { id: "full", label: "Full control", badge: "FC", color: "#ef4444" },
];

const defaultModuleAccess = {
  dashboard: "full",
  vets: "full",
  pets: "full",
  // appointments: "full",
  billing: "full",
  inventory: "full",
  plan: "full",
  lab: "full",
  pharmacy: "full",
  reports: "full",
};

const buildInitialRolePermissions = () => {
  const next = {};
  rolesList.forEach(({ name }) => {
    next[name] = { ...defaultModuleAccess };
  });
  return next;
};

const mapApiToPermissions = (rolePermissions = []) => {
  const next = buildInitialRolePermissions();
  (Array.isArray(rolePermissions) ? rolePermissions : []).forEach((item) => {
    const name = item?.name;
    if (!name || !next[name]) return;
    next[name] = syncLinkedModules({
      ...defaultModuleAccess,
      ...(item?.modules || {}),
    });
  });
  return next;
};

const normalizeRoleModulePermissions = (payload) => ({
  clinicId: payload?.clinicId ?? null,
  rolePermissions: Array.isArray(payload?.rolePermissions)
    ? payload.rolePermissions
    : [],
});

const storeRoleModulePermissionsInSession = (payload) => {
  if (!payload) return;
  mergeMandateSessionKeepTimestamp({
    roleModulePermissions: normalizeRoleModulePermissions(payload),
  });
};

const getSessionRoleModulePermissions = () => {
  const cached = readMandateSession()?.roleModulePermissions;
  if (!cached || typeof cached !== "object") return null;
  return normalizeRoleModulePermissions(cached);
};

const RolesPermissionsSettings = () => {
  const dispatch = useDispatch();
  const branches = useMemo(() => {
    const sessionBranches = readMandateSession()?.clinicBranches;
    return Array.isArray(sessionBranches) ? sessionBranches : [];
  }, []);
  const user = localStorage.getItem("user");
  const profile = useMemo(() => {
    try {
      return user ? JSON.parse(user) : {};
    } catch {
      return {};
    }
  }, [user]);
  const globalSettingsData =
    readMandateSession()?.dataSharing?.globalSettingsData ?? false;
  const showSharedTag =
    branches?.some((li) => li?.id === profile?.id && li?.branchNumber) &&
    (globalSettingsData === true || globalSettingsData === "true");
  const { roleModulePermissions } = useSelector(
    (state) => state.globalSettings,
  );
  const [selectedRole, setSelectedRole] = useState(rolesList[0].name);
  const [permissions, setPermissions] = useState(() => {
    const cached = getSessionRoleModulePermissions();
    if (cached?.rolePermissions?.length > 0) {
      return mapApiToPermissions(cached.rolePermissions);
    }
    return buildInitialRolePermissions();
  });
  const [hasExisting, setHasExisting] = useState(() => {
    const cached = getSessionRoleModulePermissions();
    return (cached?.rolePermissions?.length ?? 0) > 0;
  });
  const hydratedRef = useRef(false);

  useEffect(() => {
    dispatch(getRoleModulePermissions({ silent: true })).then((res) => {
      if (!getRoleModulePermissions.fulfilled.match(res)) return;
      const list = Array.isArray(res.payload?.rolePermissions)
        ? res.payload.rolePermissions
        : [];
      setHasExisting(list.length > 0);
      if (list.length > 0 && !hydratedRef.current) {
        setPermissions(mapApiToPermissions(list));
        hydratedRef.current = true;
      }
      storeRoleModulePermissionsInSession(res.payload);
    });
  }, [dispatch]);

  const updatePermission = (module, level) => {
    const linkedKeys = getLinkedModules(module);
    setPermissions((prev) => {
      const rolePerms = {
        ...defaultModuleAccess,
        ...(prev[selectedRole] || {}),
      };
      linkedKeys.forEach((key) => {
        rolePerms[key] = level;
      });
      return {
        ...prev,
        [selectedRole]: rolePerms,
      };
    });
  };

  const buildSaveRequest = () => {
    const rolePermissions = rolesList.map(({ name, role }) => {
      const rolePerms = {
        ...defaultModuleAccess,
        ...(permissions[name] || {}),
      };
      const modules = {};
      modulesList.forEach(({ key }) => {
        modules[key] = rolePerms[key] || "full";
      });
      // Persist linked modules together (dashboard ↔ pets; appointments local-only)
      LINKED_MODULE_GROUPS.forEach((keys) => {
        const level =
          keys.map((key) => modules[key] ?? rolePerms[key]).find(Boolean) ||
          "full";
        keys.forEach((key) => {
          modules[key] = level;
        });
      });
      // Ensure newly added modules are always present in the payload
      modules.lab = rolePerms.lab || "full";
      modules.pharmacy = rolePerms.pharmacy || "full";
      const synced = syncLinkedModules(modules);
      // Do not send appointments / globalSettings in the API payload
      delete synced.appointments;
      return { name, role, modules: synced };
    });
    return { rolePermissions };
  };

  const handleSave = () => {
    const payload = buildSaveRequest();
    dispatch(
      hasExisting
        ? updateRoleModulePermissions(payload)
        : createRoleModulePermissions(payload),
    ).then((res) => {
      const matched = hasExisting
        ? updateRoleModulePermissions.fulfilled.match(res)
        : createRoleModulePermissions.fulfilled.match(res);
      if (!matched) return;

      setHasExisting(true);
      setPermissions(mapApiToPermissions(payload.rolePermissions));

      const clinicId =
        res.payload?.clinicId ??
        roleModulePermissions?.clinicId ??
        getSessionRoleModulePermissions()?.clinicId ??
        null;
      storeRoleModulePermissionsInSession({
        clinicId,
        rolePermissions: payload.rolePermissions,
      });

      dispatch(getRoleModulePermissions({ silent: true })).then((getRes) => {
        if (!getRoleModulePermissions.fulfilled.match(getRes)) return;
        storeRoleModulePermissionsInSession(getRes.payload);
        const list = Array.isArray(getRes.payload?.rolePermissions)
          ? getRes.payload.rolePermissions
          : [];
        if (list.length > 0) {
          setHasExisting(true);
          setPermissions(mapApiToPermissions(list));
        }
      });
    });
  };

  const currentRolePerms = permissions[selectedRole] || {};

  return (
    <div className="consultation-white-con scroll-80vh p20">
      <div className="flex-row-between-align-center mb20 pb10 border-bottom">
        <div>
          <div className="flex-row-ali-cen">
            <Typography className="text-bold fs20 black">
              Roles & Permissions
            </Typography>
            {showSharedTag ? (
              <Typography className="white-color fs12 con-fit card-top-orange-color ph10 br10 ml10">
                Organisation default
              </Typography>
            ) : null}
          </div>
          <Typography className="txt-regular fs13 card-gray-color mt4 rp-subtitle">
            Standard roles ship with sensible defaults. Adjust module access per
            role for this branch; the role set itself is fixed platform-wide so
            behaviour and support stay predictable.
          </Typography>
        </div>
        <div>
          <CustomButton
            text="Save Changes"
            onClick={handleSave}
            smallBtn
            disabled={showSharedTag}
            grayBtn={showSharedTag}
          />
        </div>
      </div>

      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <div className="rp-roles-card">
            {rolesList.map((r) => (
              <button
                type="button"
                key={r.role + r.name}
                className={`rp-role-item ${
                  selectedRole === r.name ? "rp-role-item-active" : ""
                }`}
                onClick={() => setSelectedRole(r.name)}
              >
                {r.name}
              </button>
            ))}
          </div>
        </Grid>

        <Grid item xs={12} md={9}>
          <div className="rp-perms-card">
            <div className="flex-row-between-align-center mb15">
              <Typography className="text-bold fs16 black">
                {selectedRole}
              </Typography>

              <div className="rp-legend">
                {accessLevels.map((lvl) => (
                  <span className="rp-legend-item" key={lvl.id}>
                    <span
                      className="rp-legend-dot"
                      style={{ backgroundColor: lvl.color }}
                    />
                    {lvl.label}
                  </span>
                ))}
              </div>
            </div>

            <div className="rp-module-list">
              {modulesList.map((mod) => {
                const currentLevel = currentRolePerms[mod.key] || "full";

                return (
                  <div key={mod.key} className="rp-module-row">
                    <div className="rp-module-name">
                      <span>{mod.label}</span>
                    </div>
                    <div className="rp-access-group">
                      {accessLevels.map((lvl) => {
                        const isSelected = currentLevel === lvl.id;
                        return (
                          <button
                            type="button"
                            key={lvl.id}
                            className={`rp-access-btn ${
                              isSelected ? "rp-access-btn-active" : ""
                            }`}
                            style={
                              isSelected
                                ? {
                                    backgroundColor: lvl.color,
                                    borderColor: lvl.color,
                                    color: "#fff",
                                  }
                                : undefined
                            }
                            onClick={() => updatePermission(mod.key, lvl.id)}
                            title={lvl.label}
                            disabled={showSharedTag}
                          >
                            {lvl.badge}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Grid>
      </Grid>
    </div>
  );
};

export default RolesPermissionsSettings;
